<script setup lang="ts">
/**
 * Pattern panel (brief §4–5) — replaces the legacy PatternPicker.
 *
 * Three sections:
 *   1. Favourites — the user's saved configs (always selectable).
 *   2. By shape — system presets grouped by tile shape; pro presets show a
 *      lock and open the paywall.
 *   3. Configure pattern — an inline expandable settings panel for whatever
 *      pattern is currently active (picked via 1 or 2), tweaked live with no
 *      separate "create/apply" step. A heart button next to it saves the
 *      current state as a favourite (or overwrites it in place if it already
 *      is one).
 */
import {
	IconGhost2,
	IconHeart,
	IconHeartFilled,
	IconLock,
	IconRotate2,
	IconRotate360,
	IconRotateClockwise2,
	IconStarFilled,
	IconX,
	IconHexagon,
} from '@tabler/icons-vue'
import {
	ARTIST_SHAPE_ORDER,
	PARAM_RANGES,
	ROTATION_SECTIONS,
	SYSTEM_PRESETS,
	colourCountsFor,
	configSummary,
	getWallpaperGroup,
	isWpgFree,
	rotationStepRadians,
	type ArtistShapeId,
	type PatternConfig,
	type SystemPreset,
} from '@/patterns/wallpaper'
import { useLayoutMode } from '@/composables/useLayoutMode'
import { useCanvasStore } from '@/stores/canvas'
import { useEntitlementStore } from '@/stores/entitlement'
import { useFavouritesStore, type NewFavourite } from '@/stores/favourites'
import { usePatternStore } from '@/stores/pattern'
import { useToolsStore } from '@/stores/tools'
import { useUiStore } from '@/stores/ui'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import WpgThumbnail from './WpgThumbnail.vue'

const patternStore = usePatternStore()
const favourites = useFavouritesStore()
const entitlement = useEntitlementStore()
const canvasStore = useCanvasStore()
const tools = useToolsStore()
const { isInlinePanel } = useLayoutMode()
const ui = useUiStore()

// ── Colour symmetry (brief 09-layout: moved here from the toolbar — how many
// colours tile across the pattern is a tessellation-scheme concern, not a
// drawing concern; the pencil colour PICKER stays in the bottom toolbar).
// Brief 10-colours exposes exactly off/2/3/4/6 — some groups (p3, p6) also
// have a valid 7-colouring (classifyColour/COLOUR_COUNTS keep it, tested),
// but it's intentionally not offered in this control. ──
const EXPOSED_COLOUR_SYMMETRIES = [1, 2, 3, 4, 6]
const availableColourCounts = computed(() =>
	colourCountsFor(patternStore.currentConfig.wpgId).filter((n) => EXPOSED_COLOUR_SYMMETRIES.includes(n)),
)

// ── Transform: rotate the CANVAS VIEW (not the pattern's own geometry) by one
// symmetry-snap step at a time — replaces the two-finger touch gesture (brief
// 09-layout §mobile), also available on desktop. The step is a property of
// the current drawing tile's actual shape (brief 11-layout2 §1, refined —
// a regular hexagon or equilateral triangle snaps at 60°, everything else
// (including a right-angled/isosceles triangle) at 45°), not the wallpaper
// group id or lattice family alone — see rotationStepRadians.
const rotationStep = computed(() => rotationStepRadians(patternStore.currentPattern.polygon()))
function rotateCW(): void {
	canvasStore.rotateBy(rotationStep.value, 1)
}
function rotateCCW(): void {
	canvasStore.rotateBy(rotationStep.value, -1)
}

onMounted(() => {
	if (!favourites.loaded) void favourites.refresh()
})

// ── Section data ─────────────────────────────────────────────────────────────

/** Simple tile glyph per artist-shape group, shown on the section header. */
const GROUP_ICON: Record<ArtistShapeId, string> = {
	hexagon: 'M12 2l8 5v10l-8 5-8-5V7z',
	triangle: 'M12 4 20 20 4 20Z',
	square: 'M4 4h16v16H4z',
	rectangle: 'M3 7h18v10H3z',
	rhombus: 'M12 3l8 9-8 9-8-9z',
	parallelogram: 'M8 5h12l-4 14H4z',
	assorted: 'M12 2l3 8h8l-6 4 2 8-7-5-7 5 2-8-6-4h8z',
}

const presetsByShape = computed(() =>
	ARTIST_SHAPE_ORDER.map((g) => ({
		id: g.id,
		label: g.label,
		presets: SYSTEM_PRESETS.filter((p) => p.section === g.id),
	})).filter((s) => s.presets.length > 0),
)

const sortByRotation = ref(false)

const presetsByRotation = computed(() =>
	ROTATION_SECTIONS.map((section) => ({
		order: section.order,
		label: section.label,
		presets: SYSTEM_PRESETS.filter((p) => {
			const group = getWallpaperGroup(p.wpgId)
			return group.rotationOrder === section.order
		}),
	})).filter((s) => s.presets.length > 0),
)

const activeConfigId = computed(() => patternStore.currentConfig?.id ?? null)

// ── Preview carousel (brief 11-layout2 §2, extended by the follow-up addendum
// to also apply in inline mode): swipeable, paged (not free-scroll — CSS
// scroll-snap, not a custom drag handler) browse through the available
// presets, independent of the tap-to-select grid below it ("swipe is for
// browsing, the list is for 'I know which one I want'"). Only entitled
// (unlocked) presets are included — a locked one can still be reached via the
// grid below, which shows its paywall lock explicitly, but silently swiping
// past/onto one mid-browse isn't a good fit for a fast browsing gesture.
// Deferred ("coming soon") presets stay in, same as the grid — they're just
// not selectable. Used in both docked and inline layouts; only the preview
// section's CSS sizing differs between them. ──
const carouselPresets = computed(() => SYSTEM_PRESETS.filter((p) => !presetLocked(p)))
const carouselRef = ref<HTMLElement | null>(null)
const carouselIndex = ref(0)
let carouselSettleTimer: ReturnType<typeof setTimeout> | null = null

function onCarouselScroll(): void {
	if (carouselSettleTimer) clearTimeout(carouselSettleTimer)
	carouselSettleTimer = setTimeout(settleCarousel, 120)
}

function settleCarousel(): void {
	const el = carouselRef.value
	if (!el || el.clientWidth === 0) return
	const index = Math.min(
		carouselPresets.value.length - 1,
		Math.max(0, Math.round(el.scrollLeft / el.clientWidth)),
	)
	carouselIndex.value = index
	const preset = carouselPresets.value[index]
	if (preset && preset.id !== activeConfigId.value) pickPreset(preset)
}

/** Keep the carousel positioned on whatever's active, including changes made
 *  elsewhere (favourites, the grid below, Configure pattern's own controls). */
watch(
	() => activeConfigId.value,
	(id) => {
		const index = carouselPresets.value.findIndex((p) => p.id === id)
		if (index === -1) return
		carouselIndex.value = index
		void nextTick(() => {
			carouselRef.value?.scrollTo({ left: index * carouselRef.value.clientWidth, behavior: 'auto' })
		})
	},
	{ immediate: true },
)

function presetLocked(config: PatternConfig): boolean {
	return !isWpgFree(config.wpgId) && !entitlement.isPro
}

function presetDeferred(preset: SystemPreset | PatternConfig): boolean {
	return !!(preset as any).deferred
}

function configSummaryByRotation(config: PatternConfig): string {
	const group = getWallpaperGroup(config.wpgId)
	const rotationLabel =
		ROTATION_SECTIONS.find((s) => s.order === group.rotationOrder)?.label ??
		'Unknown'
	const parts = [rotationLabel, config.label, `(${config.wpgId})`]
	if (config.latticeParams.aspectRatio !== undefined) {
		parts.push(`ratio ${config.latticeParams.aspectRatio.toFixed(2)}`)
	}
	if (config.latticeParams.angle !== undefined) {
		parts.push(`${Math.round(config.latticeParams.angle)}°`)
	}
	return parts.join(' · ')
}

function pickPreset(config: PatternConfig): void {
	if (presetLocked(config)) {
		ui.openPaywall('pattern')
		return
	}
	if (presetDeferred(config as SystemPreset)) {
		return // Deferred patterns are not clickable
	}
	patternStore.setConfig(config)
}

function pickFavourite(config: PatternConfig): void {
	// user's own saved config — always selectable
	patternStore.applyConfig(config)
}

// ── Delete a favourite (Bug 3) — built-in/system symmetries never appear in
// favourites.favourites (the store always tags loaded entries isSystem:false),
// but the check is kept explicit so the delete affordance can never appear on
// one even if that changes. Reuses the same confirm-dialog pattern as the
// replace/unsaved-config dialogs above rather than inventing a new one.
const deleteTarget = ref<PatternConfig | null>(null)

function requestDeleteFavourite(config: PatternConfig): void {
	if (config.isSystem) return
	deleteTarget.value = config
}

async function confirmDeleteFavourite(): Promise<void> {
	if (!deleteTarget.value) return
	await favourites.remove(deleteTarget.value.id)
	deleteTarget.value = null
}

// ── Collapsible shape sections (first open by default) ──────────────────────

const openShapeGroups = reactive<Record<string, boolean>>({})
presetsByShape.value.forEach((s, i) => {
	openShapeGroups[s.label] = i === 0
})
/** Accordion: opening a section closes the others (one open at a time). */
function toggleGroup(label: string): void {
	const willOpen = !openShapeGroups[label]
	for (const key of Object.keys(openShapeGroups)) openShapeGroups[key] = false
	openShapeGroups[label] = willOpen
}

// ── Configure current pattern (inline, no separate create/apply step) ──────

/** Which sliders apply to the current pattern's lattice. */
const paramRange = computed(() => {
	const cfg = patternStore.currentConfig
	if (
		cfg.artistTile === 'hexagon' ||
		(cfg.artistTile === 'rhombus' && cfg.wpgId === 'p3')
	) {
		// 3-way rhombus has fixed angle, treat like a hexagon
		return PARAM_RANGES['hexagonal']
	} else if (cfg.artistTile === 'rhombus') {
		return PARAM_RANGES['centred-rect']
	} else if (cfg.artistTile === 'parallelogram') {
		return PARAM_RANGES['oblique']
	}
	return PARAM_RANGES[getWallpaperGroup(cfg.wpgId).lattice]
})

const aspectRatio = computed<number>({
	get: () => patternStore.currentConfig.latticeParams.aspectRatio ?? 1,
	set: (v) =>
		patternStore.applyConfig({
			...patternStore.currentConfig,
			latticeParams: { ...patternStore.currentConfig.latticeParams, aspectRatio: v },
		}),
})

const angle = computed<number>({
	get: () => patternStore.currentConfig.latticeParams.angle ?? 90,
	set: (v) =>
		patternStore.applyConfig({
			...patternStore.currentConfig,
			latticeParams: { ...patternStore.currentConfig.latticeParams, angle: v },
		}),
})

// ── Heart → save as favourite (or overwrite in place if already one) ──────

const isFavourited = computed(
	() =>
		!patternStore.currentConfig.isSystem &&
		favourites.favourites.some((f) => f.id === patternStore.currentConfig.id),
)

const showSaveDialog = ref(false)
const saveName = ref('')
const showReplace = ref(false)
const saving = ref(false)

const existingFavLabel = computed(() => favourites.favourites[0]?.label ?? '')

/** Payload built at save time, held while the replace dialog is open. */
let pendingPayload: NewFavourite | null = null

function openSaveDialog(): void {
	saveName.value = isFavourited.value ? patternStore.currentConfig.label : ''
	showSaveDialog.value = true
}

function closeSaveDialog(): void {
	showSaveDialog.value = false
}

// ── Name char-count warnings (brief §5.3) ───────────────────────────────────

const nameWarning = computed(() => {
	const n = saveName.value.length
	if (n <= 20) return { level: 'ok' as const, message: '' }
	if (n <= 28) return { level: 'amber' as const, message: `${n} characters` }
	if (n <= 35)
		return { level: 'red' as const, message: 'Long names may be clipped' }
	return { level: 'red' as const, message: 'Too long — please shorten' }
})

const nameTooLong = computed(() => saveName.value.length > 35)

async function confirmSave(): Promise<void> {
	if (nameTooLong.value || !saveName.value.trim()) return
	const payload: NewFavourite = {
		label: saveName.value.trim(),
		wpgId: patternStore.currentConfig.wpgId,
		latticeParams: { ...patternStore.currentConfig.latticeParams },
		artistTile: patternStore.currentConfig.artistTile,
	}
	saving.value = true
	let saved: PatternConfig | null
	if (isFavourited.value) {
		saved = await favourites.update(patternStore.currentConfig.id, payload)
	} else if (!favourites.canAddMore) {
		saving.value = false
		showSaveDialog.value = false
		pendingPayload = payload
		showReplace.value = true
		return
	} else {
		saved = await favourites.add(payload)
	}
	saving.value = false
	showSaveDialog.value = false
	if (saved) patternStore.applyConfig(saved)
}

async function commitReplace(): Promise<void> {
	if (!pendingPayload) return
	saving.value = true
	const saved = await favourites.replace(pendingPayload)
	saving.value = false
	showReplace.value = false
	pendingPayload = null
	patternStore.applyConfig(saved)
}

function keepExisting(): void {
	showReplace.value = false
	pendingPayload = null
}
</script>

<template>
	<div class="pattern-panel" :class="{ inline: isInlinePanel }">
		<!-- Current pattern — drawn larger, the distinctive Escherllate feature
		     (brief 09-layout: "make it visually important"). A height-capped,
		     swipeable/paged carousel to browse the available presets (brief
		     11-layout2 §2, now in both docked AND inline layouts) — the
		     tap-to-select grid further down is unaffected, this is just a
		     faster way to flip through options. Inline sizing (sitting beside
		     Configure pattern's controls) is pure CSS, see .pattern-panel.inline
		     below. -->
		<section class="panel-section current-preview-section">
			<div ref="carouselRef" class="preview-carousel" @scroll="onCarouselScroll">
				<div v-for="preset in carouselPresets" :key="preset.id" class="carousel-page">
					<WpgThumbnail
						:wpg-id="preset.wpgId"
						:lattice-params="preset.latticeParams"
						:artist-tile="preset.artistTile"
						:fill="true"
					/>
				</div>
			</div>
			<p class="current-preview-label">
				{{ patternStore.currentConfig.label }}
				<span class="current-preview-id">({{ patternStore.currentConfig.wpgId }})</span>
			</p>
			<p class="carousel-indicator">{{ carouselIndex + 1 }} of {{ carouselPresets.length }}</p>
		</section>

		<!-- Favourites -->
		<section v-if="favourites.favourites.length" class="panel-section favourites-section">
				<h2>Favourites</h2>
				<div class="pill-grid">
					<button
						v-for="fav in favourites.favourites"
						:key="fav.id"
						class="pill"
						:class="{ active: fav.id === activeConfigId }"
						:title="configSummary(fav)"
						@click="pickFavourite(fav)"
					>
						<WpgThumbnail
							:wpg-id="fav.wpgId"
							:lattice-params="fav.latticeParams"
							:artist-tile="fav.artistTile"
							:fill="true"
						/>
						<span class="pill-name">{{ fav.label }}</span>
						<IconStarFilled :size="12" class="pill-star" />
						<span
							v-if="!fav.isSystem"
							class="pill-delete"
							role="button"
							tabindex="0"
							title="Remove favourite"
							@click.stop="requestDeleteFavourite(fav)"
							@keydown.enter.stop="requestDeleteFavourite(fav)"
							@keydown.space.stop.prevent="requestDeleteFavourite(fav)"
						>
							<IconX :size="11" stroke-width="2.5" />
						</span>
					</button>
				</div>
			</section>

			<!-- By shape / By rotation toggle -->
			<section class="panel-section byshape-section">
				<div
					style="
						display: flex;
						justify-content: space-between;
						align-items: center;
						margin-bottom: 12px;
					"
				>
					<h2 style="margin: 0">
						{{ sortByRotation ? 'By rotation' : 'By shape' }}
					</h2>
					<button
						class="sort-toggle"
						:title="`Sort by ${sortByRotation ? 'shape' : 'rotation symmetry'}`"
						@click="sortByRotation = !sortByRotation"
					>
						<IconHexagon :size="14" stroke-width="2" v-if="sortByRotation" />
						<IconRotateClockwise2 :size="14" stroke-width="2" v-else />
						{{ sortByRotation ? 'Shape' : 'Rotation' }}
					</button>
				</div>

				<!-- By shape view -->
				<template v-if="!sortByRotation">
					<div
						v-for="grp in presetsByShape"
						:key="grp.label"
						class="shape-group"
					>
						<button
							class="group-head"
							:aria-expanded="openShapeGroups[grp.label]"
							@click="toggleGroup(grp.label)"
						>
							<span class="group-head-label">
								<svg class="group-icon" viewBox="0 0 24 24" aria-hidden="true">
									<path
										:d="GROUP_ICON[grp.id]"
										fill="none"
										stroke="currentColor"
										stroke-width="1.6"
									/>
								</svg>
								{{ grp.label }}
							</span>
							<span class="group-count">{{ grp.presets.length }}</span>
						</button>
						<div v-show="openShapeGroups[grp.label]" class="pill-grid">
							<button
								v-for="preset in grp.presets"
								:key="preset.id"
								class="pill"
								:class="{
									active: preset.id === activeConfigId,
									locked: presetLocked(preset),
									deferred: presetDeferred(preset),
								}"
								:disabled="presetDeferred(preset)"
								:title="configSummary(preset)"
								@click="pickPreset(preset)"
							>
								<WpgThumbnail
									:wpg-id="preset.wpgId"
									:lattice-params="preset.latticeParams"
									:artist-tile="preset.artistTile"
									:fill="true"
								/>
								<span class="pill-name">{{ preset.label }}</span>
								<IconLock
									v-if="presetLocked(preset)"
									:size="12"
									class="pill-lock"
								/>
								<span v-if="presetDeferred(preset)" class="pill-badge"
									>Coming soon</span
								>
							</button>
						</div>
					</div>
				</template>

				<!-- By rotation view -->
				<template v-else>
					<div
						v-for="grp in presetsByRotation"
						:key="grp.order"
						class="shape-group"
					>
						<button
							class="group-head"
							:aria-expanded="openShapeGroups[grp.label]"
							@click="toggleGroup(grp.label)"
						>
							<span class="group-head-label">{{ grp.label }}</span>
							<span class="group-count">{{ grp.presets.length }}</span>
						</button>
						<div v-show="openShapeGroups[grp.label]" class="pill-grid">
							<button
								v-for="preset in grp.presets"
								:key="preset.id"
								class="pill"
								:class="{
									active: preset.id === activeConfigId,
									locked: presetLocked(preset),
									deferred: presetDeferred(preset),
								}"
								:disabled="presetDeferred(preset)"
								:title="configSummaryByRotation(preset)"
								@click="pickPreset(preset)"
							>
								<WpgThumbnail
									:wpg-id="preset.wpgId"
									:lattice-params="preset.latticeParams"
									:artist-tile="preset.artistTile"
									:fill="true"
								/>
								<span class="pill-name">{{ preset.label }}</span>
								<IconLock
									v-if="presetLocked(preset)"
									:size="12"
									class="pill-lock"
								/>
								<span v-if="presetDeferred(preset)" class="pill-badge"
									>Coming soon</span
								>
							</button>
						</div>
					</div>
				</template>
			</section>

			<!-- Configure pattern — inline settings for whatever's active now,
			     always visible (no expand/collapse step). -->
			<section class="panel-section configure-section">
				<div class="configure-header">
					<h2>Configure pattern</h2>
					<button
						class="heart-btn"
						:class="{ active: isFavourited }"
						title="Save as favourite"
						@click="openSaveDialog"
					>
						<IconHeartFilled v-if="isFavourited" :size="16" />
						<IconHeart v-else :size="16" stroke-width="2" />
					</button>
				</div>

				<div class="configure-body">
					<label v-if="paramRange.aspectRatio" class="slider-row">
						<span class="slider-label"
							>Proportion <em>{{ aspectRatio.toFixed(2) }}</em></span
						>
						<input
							type="range"
							:min="paramRange.aspectRatio[0]"
							:max="paramRange.aspectRatio[1]"
							step="0.01"
							v-model.number="aspectRatio"
						/>
					</label>

					<label v-if="paramRange.angle" class="slider-row">
						<span class="slider-label"
							>Angle <em>{{ Math.round(angle) }}°</em></span
						>
						<input
							type="range"
							:min="paramRange.angle[0]"
							:max="paramRange.angle[1]"
							step="1"
							v-model.number="angle"
						/>
					</label>

					<p
						v-if="!paramRange.aspectRatio && !paramRange.angle"
						class="no-params"
					>
						This lattice has no adjustable proportions.
					</p>
				</div>
			</section>

			<!-- Parameters — brief addendum: Transform, Construction visibility and
			     Colour symmetry used to be three separately-headed sections; folded
			     into one compact area (grouped rows, not three stacked blocks) since
			     none of the three need their own heading to stay legible. -->
			<section class="panel-section params-section">
				<h2>Parameters</h2>
				<div class="params-row">
					<!-- Transform — rotate the canvas VIEW (brief 09-layout §mobile:
					     replaces the two-finger touch gesture, "a button... to rotate
					     the view CW/CCW", snapping to whatever's sensible for the
					     pattern's own symmetry). -->
					<div class="transform-row">
						<button class="transform-btn" title="Rotate counter-clockwise" @click="rotateCCW">
							<IconRotate2 :size="18" stroke-width="1.75" />
						</button>
						<button class="transform-btn" title="Rotate clockwise" @click="rotateCW">
							<IconRotateClockwise2 :size="18" stroke-width="1.75" />
						</button>
						<button
							v-if="canvasStore.rotation !== 0 || canvasStore.viewMoved"
							class="transform-btn"
							title="Reset view"
							@click="canvasStore.resetView()"
						>
							<IconRotate360 :size="18" stroke-width="1.75" />
						</button>
					</div>
					<!-- Construction visibility — a tessellation setting, not a drawing
					     action (brief 09-layout: moved out of the main toolbar). -->
					<button
						class="transform-btn"
						:class="{ active: tools.showGhosts }"
						:title="`${tools.showGhosts ? 'Hide' : 'Show'} tessellated copies`"
						@click="tools.showGhosts = !tools.showGhosts"
					>
						<IconGhost2 :size="16" stroke-width="1.75" />
					</button>
				</div>
				<!-- Colour symmetry — how many pencil colours tile across the pattern
				     (a tessellation-scheme setting; the colour PICKER itself lives in
				     the bottom toolbar's Colour control). -->
				<div v-if="availableColourCounts.length > 1" class="group seg colour-count-seg" title="How many pencil colours tile across the pattern">
					<button
						v-for="n in availableColourCounts"
						:key="n"
						class="count-btn"
						:class="{ active: tools.colourCount === n }"
						:title="n === 1 ? 'Off — single colour' : `${n}-way colour symmetry`"
						@click="tools.setColourCount(n)"
					>
						{{ n === 1 ? 'Off' : n }}
					</button>
				</div>
			</section>

		<!-- ───────────── Save as favourite (name prompt) ───────────── -->
		<div
			v-if="showSaveDialog"
			class="dialog-backdrop"
			@click.self="closeSaveDialog"
		>
			<div class="dialog">
				<h3>{{ isFavourited ? 'Update favourite' : 'Save as favourite' }}</h3>
				<label class="name-row">
					<span class="slider-label">Name</span>
					<input
						type="text"
						class="name-input"
						:class="nameWarning.level"
						v-model="saveName"
						maxlength="48"
						placeholder="e.g. My snowflake"
					/>
					<span
						v-if="nameWarning.message"
						class="name-warn"
						:class="nameWarning.level"
						>{{ nameWarning.message }}</span
					>
				</label>
				<div class="dialog-actions">
					<button
						class="dialog-primary"
						:disabled="!saveName.trim() || nameTooLong || saving"
						@click="confirmSave"
					>
						{{ saving ? 'Saving…' : isFavourited ? 'Update' : 'Save' }}
					</button>
					<button class="dialog-secondary" @click="closeSaveDialog">
						Cancel
					</button>
				</div>
			</div>
		</div>

		<!-- ───────────── Replace dialog (free 2nd save) ───────────── -->
		<div
			v-if="showReplace"
			class="dialog-backdrop"
			@click.self="keepExisting"
		>
			<div class="dialog">
				<h3>Replace your favourite?</h3>
				<p>
					Free includes one saved pattern. You currently have
					<strong>{{ existingFavLabel }}</strong
					>. Replace it with this new one?
				</p>
				<div class="dialog-actions">
					<button class="dialog-primary" @click="commitReplace">
						Replace and save
					</button>
					<button class="dialog-secondary" @click="keepExisting">
						Keep my existing one
					</button>
				</div>
			</div>
		</div>

		<!-- ───────────── Delete favourite confirmation (Bug 3) ───────────── -->
		<div
			v-if="deleteTarget"
			class="dialog-backdrop"
			@click.self="deleteTarget = null"
		>
			<div class="dialog">
				<h3>Remove favourite?</h3>
				<p>
					<strong>{{ deleteTarget.label }}</strong> will be removed from your
					favourites. This can't be undone.
				</p>
				<div class="dialog-actions">
					<button class="dialog-primary" @click="confirmDeleteFavourite">
						Remove
					</button>
					<button class="dialog-secondary" @click="deleteTarget = null">
						Cancel
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.pattern-panel {
	padding: 12px;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
}

/* Narrow/docked stacking order (brief 14-even_closer §Tessellation panel):
   WPG swipe → pattern config → rotate/reset/ghosts → favourites → WPG gallery.
   The DOM order is left untouched; flex `order` drives the visual order here.
   Inline (tablet portrait) mode below overrides layout entirely via grid
   areas, where `order` is ignored. */
.current-preview-section {
	order: 1;
}
.configure-section {
	order: 2;
}
.params-section {
	order: 3;
}
.favourites-section {
	order: 4;
}
.byshape-section {
	order: 5;
}

.panel-section {
	margin-bottom: 14px;
}

.pattern-panel h2 {
	font-family: var(--font-mono);
	font-size: 11px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.12em;
	color: var(--text-muted);
	margin: 0 0 8px;
}

.pill-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(78px, 1fr));
	gap: 6px;
}

.pill {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	justify-content: flex-end;
	gap: 0;
	padding: 0;
	background: transparent;
	border: 1px solid var(--ui-border);
	border-radius: 8px;
	color: var(--text-primary);
	cursor: pointer;
	position: relative;
	overflow: hidden;
	aspect-ratio: 1;
	min-height: 72px;
}

.pill:disabled {
	cursor: not-allowed;
}

.pill:hover {
	background: rgba(122, 122, 204, 0.08);
}

.pill.active {
	border-color: var(--accent);
	background: rgba(122, 122, 204, 0.12);
}

.pill.locked {
	color: var(--text-muted);
}

.pill.locked::after {
	content: '';
	position: absolute;
	inset: 0;
	background: rgba(255, 255, 255, 0.12);
	backdrop-filter: blur(3px);
	pointer-events: none;
	border-radius: 7px;
	z-index: 3;
}

.pill.deferred {
	color: var(--text-muted);
}

.pill.deferred::after {
	content: '';
	position: absolute;
	inset: 0;
	background: rgba(200, 180, 255, 0.08);
	backdrop-filter: blur(5px);
	pointer-events: none;
	border-radius: 7px;
	z-index: 2;
}

.pill-name {
	position: relative;
	z-index: 5;
	font-size: 10.5px;
	line-height: 1.2;
	text-align: center;
	word-break: break-word;
	padding: 3px 6px 4px;
	background: rgba(0, 0, 0, 0.55);
	color: #fff;
	backdrop-filter: blur(2px);
	width: 100%;
	flex-shrink: 0;
}

.pill-star {
	position: absolute;
	top: 4px;
	right: 4px;
	z-index: 2;
	color: var(--accent);
}

/* Bug 3 — delete affordance on user-saved favourites only, opposite the star. */
.pill-delete {
	position: absolute;
	top: 4px;
	left: 4px;
	z-index: 6;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	color: #fff;
	background: rgba(0, 0, 0, 0.5);
	cursor: pointer;
}

.pill-delete:hover,
.pill-delete:focus-visible {
	background: var(--danger);
	outline: none;
}

.pill-lock {
	position: absolute;
	top: 4px;
	right: 4px;
	z-index: 5;
	/* color: var(--accent-contrast); */
	color: #fdfaf1;
}

.pill-badge {
	position: absolute;
	top: 4px;
	left: 4px;
	z-index: 4;
	font-size: 8px;
	font-weight: 600;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: #c4b4ff;
	background: rgba(150, 130, 200, 0.35);
	padding: 2px 5px;
	border-radius: 3px;
	backdrop-filter: blur(2px);
}
.sort-toggle {
	background: var(--ui-border);
	border: 1px solid var(--ui-border);
	color: var(--text-secondary);
	font-size: 11px;
	font-weight: 600;
	padding: 4px 8px;
	border-radius: 4px;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 4px;
}

.sort-toggle:hover {
	background: var(--accent);
	border-color: var(--accent);
	color: white;
}
.shape-group {
	margin-bottom: 8px;
}

.group-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	min-height: 32px;
	padding: 4px 6px;
	background: transparent;
	border: none;
	color: var(--text-primary);
	font-size: 12px;
	cursor: pointer;
}

.group-head-label {
	display: flex;
	align-items: center;
	gap: 7px;
}

.group-icon {
	width: 16px;
	height: 16px;
	flex-shrink: 0;
	color: var(--text-muted);
}

.group-count {
	font-family: var(--font-mono);
	font-size: 10px;
	color: var(--text-muted);
}

.configure-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 6px;
	margin-top: 4px;
}

.configure-header h2 {
	margin: 0;
}

.heart-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	min-height: 40px;
	flex-shrink: 0;
	background: transparent;
	border: 1px solid var(--ui-border);
	border-radius: 8px;
	color: var(--text-primary);
	cursor: pointer;
}

.heart-btn:hover {
	background: rgba(122, 122, 204, 0.08);
}

.heart-btn.active {
	border-color: var(--accent);
	color: var(--accent);
}

.configure-body {
	margin-top: 10px;
	padding: 10px;
	background: var(--ui-surface);
	border: 1px solid var(--ui-border);
	border-radius: 8px;
}

.slider-row,
.name-row {
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-bottom: 12px;
}

.slider-label {
	font-size: 12px;
	color: var(--text-muted);
}

.slider-label em {
	font-style: normal;
	color: var(--text-primary);
	font-family: var(--font-mono);
}

.slider-row input[type='range'] {
	width: 100%;
	accent-color: var(--accent);
}

.name-input {
	width: 100%;
	padding: 7px 9px;
	background: var(--ui-surface);
	border: 1px solid var(--ui-border);
	border-radius: 6px;
	color: var(--text-primary);
	font-size: 13px;
}

.name-input.amber {
	border-color: #d9a441;
}

.name-input.red {
	border-color: #d35858;
}

.name-warn {
	font-size: 11px;
}

.name-warn.amber {
	color: #d9a441;
}

.name-warn.red {
	color: #d35858;
}

.no-params {
	font-size: 12px;
	color: var(--text-muted);
	margin: 0 0 12px;
}

/* Current-pattern preview — drawn larger than the pill thumbnails. */
.current-preview-section {
	margin-bottom: 16px;
}

/* Docked/narrow: height-capped by a wide-rather-than-square aspect ratio
   (brief 11-layout2 §2) — was already correct here; it only balloons once
   a panel is full-width (inline), where this same ratio is instead applied
   to a HALF-width column (see .inline .current-preview-section below), so
   its rendered height stays reasonable either way. */
.carousel-page {
	position: relative;
	overflow: hidden;
	aspect-ratio: 16 / 10;
	border: 1px solid var(--ui-border);
	border-radius: 10px;
	background: var(--canvas-bg);
}

.current-preview-label {
	margin: 6px 0 0;
	font-size: 13px;
	font-weight: 600;
	color: var(--text-primary);
	text-align: center;
}

.current-preview-id {
	font-family: var(--font-mono);
	font-weight: 400;
	font-size: 11px;
	color: var(--text-muted);
}

/* Docked preview carousel — swipeable, paged via CSS scroll-snap (not a
   custom drag handler), independent of the tap-to-select grid below it. */
.preview-carousel {
	display: flex;
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	scrollbar-width: none;
	border-radius: 10px;
}

.preview-carousel::-webkit-scrollbar {
	display: none;
}

.carousel-page {
	flex: 0 0 100%;
	scroll-snap-align: center;
	border-radius: 10px;
}

.carousel-indicator {
	margin: 2px 0 0;
	font-family: var(--font-mono);
	font-size: 11px;
	text-align: center;
	color: var(--text-muted);
}

/* Full-width inline (tablet portrait, brief 11-layout2 §2, refined by brief
   14-even_closer): the WPG swipe preview sits ALONGSIDE the other parameters
   (pattern config + rotate/reset/ghosts, stacked in the right column) rather
   than above them; the full WPG gallery (and favourites) drop below that
   combined block. Gated on the same docked-vs-inline flag as the drawer
   chrome (§1). */
.pattern-panel.inline {
	display: grid;
	grid-template-columns: minmax(200px, 38%) 1fr;
	column-gap: 16px;
	align-items: start;
	grid-template-areas:
		'preview configure'
		'preview params'
		'favourites favourites'
		'byshape byshape';
}

.pattern-panel.inline .current-preview-section {
	grid-area: preview;
	margin-bottom: 0;
}

.pattern-panel.inline .configure-section {
	grid-area: configure;
	margin-bottom: 0;
}

.pattern-panel.inline .favourites-section {
	grid-area: favourites;
}

.pattern-panel.inline .byshape-section {
	grid-area: byshape;
}

.pattern-panel.inline .params-section {
	grid-area: params;
}

/* Parameters — Transform (rotate CW/CCW + reset), Construction visibility
   (ghost toggle) and Colour symmetry folded into one compact area (brief
   addendum) instead of three separately-headed sections. */
.params-row {
	display: flex;
	align-items: center;
	gap: 6px;
	flex-wrap: wrap;
	margin-bottom: 8px;
}

.transform-row {
	display: flex;
	gap: 6px;
}

.transform-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 40px;
	min-height: 40px;
	background: transparent;
	border: 1px solid var(--ui-border);
	border-radius: 8px;
	color: var(--text-primary);
	cursor: pointer;
}

.transform-btn:hover {
	border-color: var(--accent);
	background: color-mix(in srgb, var(--accent) 10%, transparent);
}

.transform-btn.active {
	border-color: var(--accent);
	background: color-mix(in srgb, var(--accent) 12%, transparent);
}

/* Colouring symmetry — count chips (moved in from the old toolbar) */
.group {
	display: flex;
	align-items: center;
	gap: 6px;
}

.group.seg {
	background: color-mix(in srgb, var(--ui-border) 35%, transparent);
	border: 1px solid var(--ui-border);
	border-radius: 9px;
	padding: 2px;
	gap: 2px;
	width: fit-content;
}

.count-btn {
	min-width: 30px;
	min-height: 30px;
	justify-content: center;
	display: inline-flex;
	align-items: center;
	font-family: var(--font-mono);
	font-size: 12px;
	background: transparent;
	border: none;
	border-radius: 7px;
	color: var(--text-primary);
	cursor: pointer;
}

.count-btn:hover {
	background: color-mix(in srgb, var(--accent) 10%, transparent);
}

.count-btn.active {
	background: color-mix(in srgb, var(--accent) 22%, transparent);
}

.quota-warn {
	font-size: 12px;
	color: #d35858;
	margin: 0 0 8px;
}

.dialog-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 100;
	padding: 20px;
}

.dialog {
	background: var(--ui-surface);
	border: 1px solid var(--ui-border);
	border-radius: 12px;
	padding: 18px;
	max-width: 320px;
	width: 100%;
}

.dialog h3 {
	margin: 0 0 8px;
	font-size: 15px;
}

.dialog p {
	margin: 0 0 12px;
	font-size: 13px;
	color: var(--text-primary);
	line-height: 1.45;
}

.dialog-actions {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.dialog-primary {
	min-height: 40px;
	background: var(--accent);
	border: none;
	border-radius: 8px;
	color: #fff;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
}

.dialog-secondary {
	min-height: 40px;
	background: transparent;
	border: 1px solid var(--ui-border);
	border-radius: 8px;
	color: var(--text-primary);
	font-size: 13px;
	cursor: pointer;
}
</style>
